import React from 'react';
import {Dropdown, message} from 'antd';
import {
	getBoardCopyPasteShortcutHints,
	getHistoryUndoRedoShortcutHints,
} from '@/utils/platform/historyMenuShortcuts';

import {
	canServerRedo,
	canServerUndo,
	executeCollaborationRedo,
	executeCollaborationUndo,
} from '@/hooks/diagram/useUndoRedoShortcuts';
import {isCanvasOnlyNodeType} from '@core_x6/lib/util/CanvasNodeTypes';

export default function ArchiX6ContextMenu({
	contextMenu,
	modeler,
	diagramCollaboration,
	onClose,
	onOpenViewpoint,
	onOpenProperties,
	onDeleteNodeFromModel,
}) {
	const contextMenuData = React.useMemo(() => {
		if (!contextMenu) return {menuItems: [], handleMenuClick: () => {}};
		const connectionTypes =
			contextMenu.type === 'connection'
				? (modeler?.getConnectionAllowedTypes?.(contextMenu.cellId) ?? [])
				: [];
		const directTypes = connectionTypes.filter(item => !item.reverse);
		const reverseTypes = connectionTypes.filter(item => item.reverse);
		const connectionTypeChildren = [];
		connectionTypes.forEach((item, idx) => {
			if (idx === directTypes.length && reverseTypes.length > 0) {
				connectionTypeChildren.push({type: 'divider'});
			}
			connectionTypeChildren.push({
				key: `type-${item.type}-${item.reverse}-${idx}`,
				label: item.reverse ? `${item.label} (обратное направление)` : item.label,
			});
		});
		const connectionItems = [
			{key: 'edit-label', label: 'Редактировать подпись'},
			...(connectionTypeChildren.length > 0
				? [{key: 'types', label: 'Типы связей', children: connectionTypeChildren}]
				: []),
			...(contextMenu.relationshipType === 'Access'
				? [
						{
							key: 'access',
							label: 'Направление Access',
							children: [
								{value: 'Read', label: 'Read'},
								{value: 'Write', label: 'Write'},
								{value: 'ReadWrite', label: 'Read/Write'},
							].map(({value, label}) => ({key: `access-${value}`, label})),
						},
					]
				: []),
			{
				key: 'order',
				label: 'Порядок',
				children: [
					{key: 'order-front', label: 'Вынести вперёд'},
					{key: 'order-back', label: 'Вынести назад'},
				],
			},
			{type: 'divider'},
			{key: 'remove-connection', label: 'Удалить связь', danger: true},
			{type: 'divider'},
			{key: 'properties', label: 'Параметры'},
		];
		const copyPasteHints = getBoardCopyPasteShortcutHints();
		const isCanvasOnly = isCanvasOnlyNodeType(contextMenu.viewElement?.type);
		const nodeItems = [
			{key: 'copy-board', label: 'Копировать', extra: copyPasteHints.copy},
			{key: 'paste-board', label: 'Вставить', extra: copyPasteHints.paste},
			{type: 'divider'},
			{
				key: 'order',
				label: 'Порядок',
				children: [
					{key: 'order-front', label: 'Вынести вперёд'},
					{key: 'order-back', label: 'Вынести назад'},
				],
			},
			{type: 'divider'},
			...(!isCanvasOnly
				? [{key: 'remove-node', label: 'Удалить с диаграммы', danger: true}]
				: []),
			{key: 'remove-node-from-model', label: isCanvasOnly ? 'Удалить' : 'Удалить из модели', danger: true},
			{type: 'divider'},
			{key: 'properties', label: 'Параметры'},
		];
		const graph = modeler?.getGraph?.();
		const collab = diagramCollaboration ?? null;
		const v = collab?.versionRef?.current;
		const canHistoryUndo = collab
			? canServerUndo(v?.historyStackCursor)
			: !!(graph && typeof graph.undo === 'function' && graph.canUndo?.());
		const canHistoryRedo = collab
			? canServerRedo(v?.historyStackCursor, v?.historyStackSize)
			: !!(graph && typeof graph.redo === 'function' && graph.canRedo?.());
		const historyHints = getHistoryUndoRedoShortcutHints();
		const blankItems = [
			{
				key: 'undo-board',
				label: 'Отменить',
				extra: historyHints.undo,
				disabled: !canHistoryUndo,
			},
			{
				key: 'redo-board',
				label: 'Повторить',
				extra: historyHints.redo,
				disabled: !canHistoryRedo,
			},
			{type: 'divider'},
			{key: 'paste-board', label: 'Вставить', extra: copyPasteHints.paste},
			{type: 'divider'},
			{key: 'viewpoint-picker', label: 'Viewpoint'},
			{
				key: 'routing',
				label: 'Маршрутизация связей',
				children: [
					{key: 'routing-normal', label: 'Произвольный (normal)'},
					{key: 'routing-manhattan', label: 'Manhattan'},
				],
			},
		];
		const menuItems =
			contextMenu.type === 'blank'
				? blankItems
				: contextMenu.type === 'connection'
					? connectionItems
					: nodeItems;
		const handleMenuClick = ({key}) => {
			const cellId = contextMenu.cellId;
			if (key === 'copy-board') {
				const ok = modeler?.copyBoardSelection?.({anchorCellId: cellId});
				if (ok) message.success('Скопировано');
				else message.info('Нет элементов для копирования');
				onClose?.();
				return;
			}
			if (key === 'paste-board') {
				const r = modeler?.pasteBoardSelection?.({
					anchorClientPoint:
						contextMenu?.x != null && contextMenu?.y != null
							? {x: contextMenu.x, y: contextMenu.y}
							: undefined,
				});
				if (r?.ok) message.success('Вставлено');
				else if (r?.reason === 'empty')
					message.info('Буфер пуст — сначала скопируйте узлы');
				onClose?.();
				return;
			}
			if (key === 'viewpoint-picker') {
				onClose?.();
				onOpenViewpoint?.();
				return;
			}
			if (key === 'undo-board') {
				if (collab && modeler) void executeCollaborationUndo(collab, modeler);
				else {
					const g = modeler?.getGraph?.();
					if (g?.canUndo?.()) g.undo();
				}
				onClose?.();
				return;
			}
			if (key === 'redo-board') {
				if (collab && modeler) void executeCollaborationRedo(collab, modeler);
				else {
					const g = modeler?.getGraph?.();
					if (g?.canRedo?.()) g.redo();
				}
				onClose?.();
				return;
			}
			if (key === 'routing-normal') {
				modeler?.setConnectionRouter?.('normal');
				onClose?.();
				return;
			}
			if (key === 'routing-manhattan') {
				modeler?.setConnectionRouter?.('manhattan');
				onClose?.();
				return;
			}
			if (key === 'properties') {
				onOpenProperties?.(cellId);
				onClose?.();
				return;
			}
			if (key === 'edit-label') {
				modeler?.openConnectionLabelEditor?.(cellId);
			} else if (key === 'order-front') {
				modeler?.bringCellToFront?.(cellId);
			} else if (key === 'order-back') {
				modeler?.sendCellToBack?.(cellId);
			} else if (key === 'remove-connection') {
				modeler?.removeConnectionFromView?.(cellId);
			} else if (key === 'remove-node') {
				modeler?.removeNodeFromView?.(cellId);
			} else if (key === 'remove-node-from-model') {
				onDeleteNodeFromModel?.(cellId);
			} else if (typeof key === 'string' && key.startsWith('access-')) {
				modeler?.updateRelationshipAccessType?.(cellId, key.replace('access-', ''));
			} else if (typeof key === 'string' && key.startsWith('type-')) {
				const typeItem = connectionTypes.find(
					(item, idx) => `type-${item.type}-${item.reverse}-${idx}` === key
				);
				if (typeItem) {
					modeler?.updateRelationshipType?.(cellId, typeItem.type, {
						reverse: typeItem.reverse,
					});
				}
			}
			onClose?.();
		};
		return {menuItems, handleMenuClick};
	}, [contextMenu, modeler, diagramCollaboration, onClose, onDeleteNodeFromModel, onOpenProperties, onOpenViewpoint]);

	if (!contextMenu || contextMenuData.menuItems.length === 0) return null;

	return (
		<Dropdown
			open
			placement="topLeft"
			getPopupContainer={() => document.body}
			menu={{
				items: contextMenuData.menuItems,
				onClick: contextMenuData.handleMenuClick,
			}}
			onOpenChange={open => !open && onClose?.()}
		>
			<div
				style={{
					position: 'fixed',
					left: contextMenu.x,
					top: contextMenu.y,
					width: 1,
					height: 1,
					pointerEvents: 'none',
					zIndex: -1,
				}}
			/>
		</Dropdown>
	);
}
